import React, { MouseEvent, useMemo } from 'react';
import { Tooltip, Box, styled } from '@mui/material';
import { StyledTableBody } from 'Components/TableBase/Table';
import {
  StyledIconButton,
  IconContainer,
  StyledCell,
  TableRow,
  Date,
} from './PartnersTableBody.styles';
import { Trash } from '@styled-icons/bootstrap';
import { Edit } from '@styled-icons/boxicons-solid';
import {
  IBasePartnerCompany,
  IPartnerCompanySummary,
} from 'FeatureModules/Partner/types/partner.types';
import Moment from 'react-moment';
import { IHeaderColumn } from 'Components/TableBase/SortableTableHead';
import { ReactComponent as PinIcon } from 'assets/img/icons/dark-pin.svg';
import PartnerTableTitle from 'FeatureModules/Partner/Components/PartnersTable/PartnersTableBody/PartnerTableTitle';
import { Booleanish } from 'types/booleanish.types';
import { EPartnerHeaderColumnEnum } from '../partnerHeaderColumns';
import defaultPartnerImage from 'assets/img/partners/default-partner-image.png';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import FlexBox from 'Components/FlexBox';

interface IProps {
  selectedPartner: IBasePartnerCompany | undefined;
  partners: IPartnerCompanySummary[];
  rowsPerPage: number;
  currentPage: number;
  isTransparent?: boolean;
  disablePagination?: boolean;
  headerColumns: IHeaderColumn[];
  onSelect: (partner: IBasePartnerCompany) => void;
  onEdit?: (partner: IBasePartnerCompany) => void;
  onDelete?: (partner: IBasePartnerCompany) => void;
}

export const Pin = styled(PinIcon)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  width: 14,
  height: 14,
  color: theme.custom.themeColors.grayScale[100],
}));

const PartnersTableBody: React.FC<IProps> = ({
  selectedPartner,
  partners,
  onSelect,
  onEdit,
  onDelete,
  rowsPerPage,
  currentPage,
  isTransparent = false,
  disablePagination,
  headerColumns,
}) => {
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const actions = [
    {
      icon: <Edit size="17" />,
      tooltip: 'Edit partner',
      onClick: onEdit,
      dataTestid: 'edit-partner-btn',
    },
    {
      icon: <Trash size="16" />,
      tooltip: 'Delete partner',
      onClick: onDelete,
      dataTestid: 'delete-partner-btn',
    },
  ];

  const displayedRows = useMemo(() => {
    if (disablePagination) {
      return partners;
    }
    return partners.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  }, [disablePagination, partners, currentPage, rowsPerPage]);

  return (
    <StyledTableBody data-testid="partners-table-body">
      {displayedRows.map((partner, index) => {
        const { id, sitesCount: siteCount } = partner;
        const labelId = `partner-table-${id}`;
        const isSelected = selectedPartner && selectedPartner.id === partner.id;

        return (
          <TableRow
            data-testid="partner-table-row"
            hover
            id={labelId}
            is-transparent={Booleanish(isTransparent)}
            key={`${id}-${index}`}
            is-selected={Booleanish(isSelected)}
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              onSelect(partner);
            }}
          >
            {headerColumns.map(({ id: column, align }) => {
              switch (column) {
                case EPartnerHeaderColumnEnum.TITLE:
                  return (
                    <PartnerTableTitle
                      key={partner.id}
                      partner={partner}
                      align={align}
                      hidePartnerInvitationStatus={isPartner}
                    />
                  );

                case EPartnerHeaderColumnEnum.SITES_COUNT:
                  return (
                    <StyledCell key={`${id}-${index}-sites`} scope="row" align={align}>
                      <Box display="flex" alignItems="center">
                        <Pin />
                        {siteCount}
                      </Box>
                    </StyledCell>
                  );

                case EPartnerHeaderColumnEnum.OWNER:
                  return (
                    <StyledCell key={`${id}-${index}-owner`} scope="row" align={align}>
                      {partner.name ? (
                        <FlexBox>
                          <Image
                            image={partner.logo}
                            width={44}
                            height={44}
                            backupImageUrl={defaultPartnerImage}
                            alt={`Logo of ${partner.name}`}
                          />
                          <Box mr={2} />
                          {partner.name}
                        </FlexBox>
                      ) : (
                        ''
                      )}
                    </StyledCell>
                  );

                case EPartnerHeaderColumnEnum.MODIFIED_TIMESTAMP:
                  return (
                    <StyledCell key={`${id}-${index}-createdTimestamp`} scope="row" align={align}>
                      <Date variant="subtitle2">
                        <Moment format="LL">{partner.modifiedTimestamp}</Moment>
                      </Date>
                    </StyledCell>
                  );

                case EPartnerHeaderColumnEnum.ACTIONS:
                  return (
                    <StyledCell
                      key={`${id}-${index}-actions`}
                      width={100}
                      scope="row"
                      align={align}
                    >
                      <IconContainer>
                        {actions.map((action, index) => {
                          return (
                            <Tooltip key={index} title={action.tooltip}>
                              <StyledIconButton
                                data-testid={action.dataTestid}
                                onClick={event => {
                                  event.stopPropagation();
                                  action.onClick?.(partner);
                                }}
                              >
                                {action.icon}
                              </StyledIconButton>
                            </Tooltip>
                          );
                        })}
                      </IconContainer>
                    </StyledCell>
                  );

                default:
                  return (
                    <StyledCell key={`${id}-${column}`} width={100} scope="row" align={align}>
                      {partner[column as keyof unknown]}
                    </StyledCell>
                  );
              }
            })}
          </TableRow>
        );
      })}
    </StyledTableBody>
  );
};

export default PartnersTableBody;
